import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search, Clock, PhoneCall, CheckCircle2, Star, Mail, MessageSquare, Bell } from "lucide-react";
import { CustomerLeadTitle } from "@/components/CustomerLeadTitle";
import { SecurityPanel } from "@/components/SecurityPanel";
import { AdSlot } from "@/components/AdSlot";
import { AppDownloadBanner } from "@/components/AppDownloadBanner";
import { ReportConductDialog } from "@/components/ReportConductDialog";
import { ReferralCard } from "@/components/ReferralCard";
import { labelFor } from "@/lib/services";

const STATUS = {
  requested: { label: "Awaiting collector", cls: "bg-[#E9C46A]/30 text-[#1E3F33]", icon: Clock },
  contacted: { label: "Collector reached out", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]", icon: PhoneCall },
  confirmed: { label: "Contact confirmed", cls: "bg-[#1E3F33]/10 text-[#1E3F33]", icon: CheckCircle2 },
};

function ReviewForm({ leadId, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", { lead_id: leadId, rating, comment });
      toast.success("Review posted — thank you!");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 border-t border-[#1E3F33]/10 pt-3 space-y-2" data-testid={`review-form-${leadId}`}>
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map((n)=>(
          <button key={n} type="button" data-testid={`review-star-${leadId}-${n}`} onClick={()=>setRating(n)} className="p-0.5">
            <Star className={`w-5 h-5 ${n<=rating?"fill-[#CC5A3A] text-[#CC5A3A]":"text-[#8A9A86]"}`}/>
          </button>
        ))}
      </div>
      <textarea data-testid={`review-comment-${leadId}`} required rows={2} className="input-soft" placeholder="How was your experience?" value={comment} onChange={(e)=>setComment(e.target.value)}/>
      <button data-testid={`review-submit-${leadId}`} disabled={busy} className="btn-primary text-xs py-2">{busy?"Posting…":"Post review"}</button>
    </form>
  );
}

import { FilterPills } from "@/components/FilterPills";

export default function CustomerDashboard() {
  const [leads, setLeads] = useState([]);
  const [reqView, setReqView] = useState("open");
  const [me, setMe] = useState(null);

  const load = async () => {
    const [{ data }, meRes] = await Promise.all([
      api.get("/leads/mine"),
      api.get("/auth/me").catch(() => null),
    ]);
    setLeads(data);
    if (meRes?.data) setMe(meRes.data);
  };
  useEffect(() => { load(); }, []);

  const toggleNotifyPref = async (ch) => {
    const current = me?.notify_prefs || { email: true, sms: true, push: true };
    const next = { ...current, [ch]: !current[ch] };
    setMe({ ...(me || {}), notify_prefs: next });
    try {
      const { data } = await api.put("/me/notify-prefs", next);
      setMe(data);
      toast.success("Notification preferences updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setMe({ ...(me || {}), notify_prefs: current });
    }
  };

  const confirm = async (id) => {
    try {
      await api.patch(`/leads/${id}/confirm`);
      toast.success("Contact confirmed — you can now leave a review");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="container-x py-12" data-testid="customer-dashboard">
      <AppDownloadBanner />
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">My account</div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">Your requests</h1>
          <p className="text-sm text-[#5C6B64] mt-2">One card per collector we've sent your information to, each linking to the collector profile.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/search" data-testid="customer-find-cta" className="btn-primary inline-flex items-center gap-2">
            <Search className="w-4 h-4"/> Find collectors
          </Link>
        </div>
      </div>

      {me && (
        <div className="card-soft mb-8" data-testid="notify-prefs-card">
          <h2 className="font-heading text-xl text-[#1E3F33] mb-1 flex items-center gap-2"><Bell className="w-4 h-4 text-[#CC5A3A]"/> Notifications</h2>
          <p className="text-xs text-[#8A9A86] mb-4">Choose how you'd like to hear about your requests, collector updates and review reminders.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { key: "email", label: "Email", Icon: Mail, desc: me.email || "No email on file" },
              { key: "sms", label: "SMS text", Icon: MessageSquare, desc: me.phone || "No phone on file" },
              { key: "push", label: "Push / in-app", Icon: Bell, desc: "Browser & app alerts" },
            ].map(({ key, label, Icon, desc }) => {
              const on = (me.notify_prefs || {})[key] !== false;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleNotifyPref(key)}
                  data-testid={`notify-pref-${key}`}
                  aria-pressed={on}
                  className={`text-left rounded-xl border p-3 transition-colors ${on ? "border-[#2D6A4F]/40 bg-[#2D6A4F]/5" : "border-[#1E3F33]/10 bg-white hover:border-[#1E3F33]/20"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-[#1E3F33]"><Icon className="w-4 h-4 text-[#2D6A4F]"/> {label}</span>
                    <span className={`w-9 h-5 rounded-full relative transition-colors ${on ? "bg-[#2D6A4F]" : "bg-[#1E3F33]/20"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
                    </span>
                  </div>
                  <p className="text-[11px] text-[#8A9A86] mt-1 truncate">{desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <FilterPills
        options={[
          { key: "open", label: `Open (${leads.filter((l) => l.status !== "confirmed").length})` },
          { key: "completed", label: `Completed (${leads.filter((l) => l.status === "confirmed").length})` },
        ]}
        value={reqView}
        onChange={setReqView}
        testidPrefix="customer-requests-view"
      />
      <div className="mt-5" />
      {reqView === "completed" && leads.filter((l) => l.status === "confirmed" && l.can_review).length > 0 && (
        <div className="card-soft mb-4 bg-[#E9C46A]/15 border border-[#E9C46A]/40 text-[#1E3F33] text-sm flex items-center gap-2" data-testid="review-reminder-banner">
          <Star className="w-4 h-4 text-[#CC5A3A] shrink-0"/> You have {leads.filter((l) => l.status === "confirmed" && l.can_review).length} connection(s) awaiting your review — leaving a review helps other customers choose with confidence.
        </div>
      )}
      {leads.filter((l) => reqView === "completed" ? l.status === "confirmed" : l.status !== "confirmed").length === 0 ? (
        <div className="card-soft text-[#5C6B64]" data-testid="leads-empty">
          {reqView === "completed" ? "No completed requests yet." : <>No open requests. <Link to="/search" className="text-[#CC5A3A] font-medium">Find a collector</Link> to get started.</>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {leads.filter((l) => reqView === "completed" ? l.status === "confirmed" : l.status !== "confirmed").map((l) => {
            const st = STATUS[l.status] || STATUS.requested;
            const Icon = st.icon;
            return (
              <div key={l.id} className="card-soft" data-testid={`my-lead-${l.id}`}>
                <CustomerLeadTitle l={l} />
                <div className="text-xs text-[#5C6B64] mt-1">{l.when_label}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(l.services||[]).map((s)=>(<span key={s} className="text-[10px] bg-[#F0EFEA] text-[#1E3F33] rounded-full px-2 py-0.5">{labelFor(s)}</span>))}
                </div>
                <div className="mt-3">
                  <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${st.cls}`} data-testid={`my-lead-status-${l.id}`}>
                    <Icon className="w-3 h-3"/> {st.label}
                  </span>
                  {l.status === "confirmed" && l.can_review && (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#E9C46A]/30 text-[#1E3F33] inline-flex items-center gap-1" data-testid={`my-lead-review-pending-${l.id}`}>
                      <Star className="w-3 h-3"/> Review pending
                    </span>
                  )}
                </div>
                {l.status === "contacted" && (
                  <button data-testid={`my-lead-confirm-${l.id}`} onClick={()=>confirm(l.id)} className="mt-3 btn-primary text-xs py-2 inline-flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3"/> Confirm contact
                  </button>
                )}
                {l.status === "confirmed" && l.can_review && (
                  <ReviewForm leadId={l.id} onDone={load} />
                )}
                {l.status === "confirmed" && !l.can_review && (
                  <div className="mt-3 text-xs text-[#2D6A4F] inline-flex items-center gap-1.5"><Star className="w-3 h-3 fill-[#CC5A3A] text-[#CC5A3A]"/> Reviewed</div>
                )}
                <div className="border-t border-[#1E3F33]/10 mt-3 pt-1">
                  <ReportConductDialog leadId={l.id} collectorName={l.collector_business_name} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-10 max-w-md">
        <SecurityPanel />
        <ReferralCard />
      </div>
      <div className="mt-8 max-w-md">
        <AdSlot placement="customer_dashboard" />
      </div>
    </div>
  );
}
